import type { GanttZoomLevel } from '../define.js';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Pixel-per-day at each zoom level (sl-p9sb.3.1 AC). These are the
 * canonical widths used by the axis renderer, the bar renderer and the
 * virtual scroller:
 *  - `day`     60 px/day → day labels fit (`Mon, Apr 17`)
 *  - `week`    20 px/day → 7 days = ~140 px label cell
 *  - `month`    6 px/day → ~30 days = ~180 px month cell
 *  - `quarter`  2 px/day → ~90 days = ~180 px quarter cell
 */
export const PX_PER_DAY_BY_ZOOM: Readonly<Record<GanttZoomLevel, number>> = {
  day: 60,
  week: 20,
  month: 6,
  quarter: 2,
};

export class TimeAxisMath {
  readonly pxPerMs: number;

  constructor(
    readonly zoom: GanttZoomLevel,
    readonly originMs: number
  ) {
    this.pxPerMs = PX_PER_DAY_BY_ZOOM[zoom] / MS_PER_DAY;
  }

  dateToX(ms: number): number {
    return (ms - this.originMs) * this.pxPerMs;
  }

  xToDate(x: number): number {
    return this.originMs + x / this.pxPerMs;
  }

  durationToWidth(durationMs: number): number {
    return durationMs * this.pxPerMs;
  }

  widthToDuration(width: number): number {
    return width / this.pxPerMs;
  }

  /** Anchor helper: midnight-UTC of the day containing `ms`. */
  static floorToDayUtc(ms: number): number {
    return Math.floor(ms / MS_PER_DAY) * MS_PER_DAY;
  }
}

export { MS_PER_DAY };
